<template>
  <div>
    <!-- fullscreen button (zoom icon) -->
    <button
      :aria-label="$t('frontend/fullscreen')"
      class="absolute top-0 right-0 mt-4 mr-4 flex size-10 items-center justify-center rounded-full bg-charcoal text-rhino shadow z-10"
      type="button"
      @click="openModal"
    >
      <fa-icon
        class="size-5"
        :icon="faMagnifyingGlassPlus"
        aria-hidden="true"
      />
    </button>

    <!-- fullscreen modal -->
    <teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          class="fixed inset-0 z-1060 flex items-center justify-center bg-onyx/80 cursor-pointer"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          @click.self="closeModal"
          @keydown.escape="closeModal"
        >
          <div
            v-if="currentItem != null"
            class="relative inline-block cursor-default"
            @click.stop
          >
            <button
              type="button"
              aria-label="Close"
              class="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-charcoal text-rhino shadow z-10"
              @click="closeModal"
            >
              <fa-icon
                class="size-5"
                :icon="faMagnifyingGlassMinus"
                aria-hidden="true"
              />
            </button>

            <strapi-image
              v-if="
                currentItem.component === 'media-gallery.image' &&
                asGalleryImage(currentItem).image
              "
              :key="asGalleryImage(currentItem).image?.id"
              :image="asGalleryImage(currentItem).image"
              alt-fallback="Slider Image"
              fluid
              image-class="max-w-[1600px] max-h-[90vh] w-auto h-auto"
            />
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from '@fortawesome/pro-solid-svg-icons';
import type { StrapiMediaGalleryItem } from '~/apollo/types/types';
import { useStrapiMediaTypes } from './features';

const props = withDefaults(
  defineProps<{
    currentItem?: StrapiMediaGalleryItem | null;
  }>(),
  {
    currentItem: null,
  },
);

const emit = defineEmits<{
  (e: 'modal-closed'): void;
}>();

const isOpen = ref(false);
const body = computed(() => (import.meta.client ? document.body : null));
const isLocked = useScrollLock(body);

const openModal = () => {
  isOpen.value = true;
  isLocked.value = true;
  nextTick(() => {
    // Focus the dialog so escape key works immediately
    const dialog = document.querySelector('[role="dialog"]') as HTMLElement;
    dialog?.focus();
  });
};

const closeModal = () => {
  isOpen.value = false;
  isLocked.value = false;
  emit('modal-closed');
};

const { asGalleryImage } = useStrapiMediaTypes();

defineExpose({
  openModal,
  closeModal,
});
</script>
